import GetAppIcon from "@mui/icons-material/GetApp";
import InfoIcon from "@mui/icons-material/Info";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { enumKeyOf } from "_shared/_utilities/enum-utils";
import Guid from "_shared/guids/guid";
import Document from "documents/document";
import { DocumentFileType } from "documents/types/document-file-type";
import UnavailableDocument from "documents/types/unavailable-documents/unavailable-document";
import React from "react";
import { defaultStyles, FileIcon } from "react-file-icon";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { WorkDocumentType } from "work/work-agreements/contracts/types";
import WorkDocument from "work/work-agreements/contracts/work-document";

const DocumentList = styled(List)(() => ({
  "&.MuiList-root": {
    marginLeft: 0,
  },
  maxWidth: "42rem",
  minWidth: "32rem",
  width: "100%",
}));
const ListSubhead = styled(ListSubheader)(() => ({
  backgroundColor: "#FFF",
  color: "#000",
  display: "flex",
  fontSize: "1.4em",
  justifyContent: "space-between",
  paddingRight: 0,
  whiteSpace: "nowrap",
}));
const NoRowsPlaceholder = styled(Typography)(({ theme }) => ({
  fontSize: "1.3em",
  paddingTop: theme.spacing(1),
}));
const DocumentIcon = styled(ListItemAvatar)(({ theme }) => ({
  marginRight: theme.spacing(1),
  minWidth: "2rem",
  maxWidth: "2rem",
}));

type DocumentListReadonlyProps = {
  className?: string;
  documents?: WorkDocument[];
  documentType: WorkDocumentType;
  downloadingFileId: Guid | null;
  commentThreads?: CommentThread[];
  conflictsCheckWaived?: boolean;
  onCommentsClicked?: (field: ProposalField, name?: string) => void;
  hideCommentsButton?: boolean;
  onDownload: (documentId: Guid) => void;
};

export default function DocumentListReadonly(props: Readonly<DocumentListReadonlyProps>) {
  const {
    className,
    documents,
    documentType,
    downloadingFileId,
    commentThreads,
    conflictsCheckWaived,
    onCommentsClicked,
    hideCommentsButton,
    onDownload,
  } = props;

  function getDocumentField(document: Document): ProposalField {
    if (documentType === WorkDocumentType.Conflicts) {
      return ProposalField.ConflictsDocument(document.id);
    }
    return documentType === WorkDocumentType.VendorPolicy
      ? ProposalField.VendorPolicy(document.id)
      : ProposalField.ClientPolicy(document.id);
  }

  function getPolicyHeader(): string {
    return documentType === WorkDocumentType.VendorPolicy ? "Vendor Policies" : "Client Policies";
  }

  function renderActionButtons(document: Document) {
    const documentField = getDocumentField(document);
    const hasCommentThread = commentThreads?.some((thread) => thread.field.isEqualTo(documentField)) ?? false;

    return (
      <>
        <Tooltip title="Download Document">
          <span>
            <IconButton
              edge="end"
              disabled={downloadingFileId !== null}
              onClick={() => onDownload(document.id)}
              size="large"
            >
              {downloadingFileId?.isEqualTo(document.id) ? (
                <CircularProgress color="primary" size={28} disableShrink />
              ) : (
                <GetAppIcon color="primary" fontSize="medium" />
              )}
            </IconButton>
          </span>
        </Tooltip>
        {!hideCommentsButton && (
          <CommentsButton
            commentThreads={commentThreads}
            field={documentField}
            hideUnreadCount
            toolTip={hasCommentThread ? "Comments" : "No Comments"}
            onCommentsClicked={
              onCommentsClicked
                ? () => {
                  onCommentsClicked?.(documentField);
                }
                : undefined
            }
          />
        )}
      </>
    );
  }

  return (
    <DocumentList disablePadding className={className}>
      <ListSubhead>
        {documentType === WorkDocumentType.Conflicts && (
          <span>
            Your Conflicts Checks
            <Tooltip
              disableFocusListener
              title="Conflicts document(s) establish potential conflicts checks required by participating clients/vendors"
              placement="bottom"
              arrow
            >
              <IconButton size="large" edge="end">
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </span>
        )}
        {(documentType === WorkDocumentType.ClientPolicy || documentType === WorkDocumentType.VendorPolicy) && (
          <span>
            {getPolicyHeader()}
            <Tooltip
              disableFocusListener
              title="Policy document(s) establish the policies required by participating clients/vendors"
              placement="bottom"
              arrow
            >
              <IconButton size="large" edge="end">
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </span>
        )}
      </ListSubhead>
      {!conflictsCheckWaived && (!documents || documents.length < 1) && (
        <ListItem key="noDocuments">
          <NoRowsPlaceholder>None</NoRowsPlaceholder>
        </ListItem>
      )}
      {!conflictsCheckWaived &&
        documents
          ?.filter((document) => !(document instanceof UnavailableDocument))
          .map((document: Document | UnavailableDocument) => {
            const availableDocument = document as Document;
            return (
              <ListItem key={document.id.value as React.Key} secondaryAction={renderActionButtons(availableDocument)}>
                <DocumentIcon>
                  <FileIcon {...getFileTypeIconProps(document)} />
                </DocumentIcon>
                <ListItemText
                  primary={availableDocument.name?.value}
                  secondary={availableDocument.created?.format("MM/DD/YY hh:mm A")}
                  sx={{ marginRight: 6 }}
                  slotProps={{
                    primary: {
                      sx: {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }
                    },
                  }}
                />
              </ListItem>
            );
          })}
    </DocumentList>
  );
}

function getFileTypeIconProps(document: Document | UnavailableDocument) {
  const extension = document instanceof UnavailableDocument
    ? enumKeyOf(DocumentFileType, DocumentFileType.UNKNOWN).toLocaleLowerCase()
    : enumKeyOf(DocumentFileType, document.fileType).toLocaleLowerCase();

  return {
    extension,
    ...defaultStyles[extension as keyof typeof defaultStyles],
  };
}
